// SalesrepDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './ChatApp.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DollarIcon = () => <span>$</span>;
const OrdersIcon = () => <span>📦</span>;

// Helper component for KPI cards
const KpiCard = ({ title, value, icon }) => (
    <div className={styles.kpiCard}>
        <div className={styles.kpiIcon}>{icon}</div>
        <div className={styles.kpiInfo}>
            <div className={styles.kpiLabel}>{title}</div>
            <div className={styles.kpiValue}>{value}</div>
        </div>
    </div>
);

// Helper component for tables
const AnalyticsTable = ({ title, data, columns }) => {
    if (!data || data.length === 0) return null;

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>{title}</h3>
            <div className={styles.tableResponsive}>
                <table className={styles.analyticsTable}>
                    <thead>
                        <tr>
                            {columns.map(col => <th key={col.key}>{col.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index}>
                                {columns.map(col => (
                                    <td key={`${col.key}-${index}`} data-label={col.label}>
                                        {col.isCurrency ? `$${Number(row[col.key] || 0).toFixed(2)}` : row[col.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const SalesrepDashboard = ({ currentUser }) => {
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchAnalytics = async () => {
        if (!currentUser) return;
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/analytics/sales_rep/${currentUser.id}`);
            setAnalytics(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to fetch analytics.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    return (
        <div className={styles.adminPageContainer}>
            <div className={styles.adminHeader}>
                <h2 className={styles.adminPageTitle}>{currentUser.name}'s Dashboard</h2>
                <button onClick={fetchAnalytics} disabled={isLoading} className={styles.refreshButton}>
                    {isLoading ? 'Refreshing...' : 'Refresh Data'}
                </button>
            </div>
            
            {isLoading && <p>Loading analytics...</p>}
            {error && <div className={styles.adminError}>{error}</div>}

            {analytics && (
                <>
                    {/* --- KPI Stat Cards --- */}
                    <div className={styles.kpiGrid}>
                        <KpiCard title="Today's Orders" value={analytics.today_orders} icon={<OrdersIcon />} />
                        <KpiCard title="Sales (This Week)" value={`$${analytics.total_sales_week.toFixed(2)}`} icon={<DollarIcon />} />
                        <KpiCard title="Sales (This Month)" value={`$${analytics.total_sales_month.toFixed(2)}`} icon={<DollarIcon />} />
                        <KpiCard title="Sales (YTD)" value={`$${analytics.total_sales_ytd.toFixed(2)}`} icon={<DollarIcon />} />
                    </div>

                    {/* --- Data Grid --- */}
                    <div className={styles.pageGrid}>
                        <AnalyticsTable
                            title="Top Retailers (by Revenue)"
                            data={analytics.top_retailers}
                            columns={[
                                { key: 'store_name', label: 'Store Name' },
                                { key: 'total_spent', label: 'Total Spent', isCurrency: true },
                            ]}
                        />
                        <AnalyticsTable
                            title="Top Products Sold (by Revenue)"
                            data={analytics.top_products}
                            columns={[
                                { key: 'product_name', label: 'Product' },
                                { key: 'total_revenue', label: 'Total Revenue', isCurrency: true },
                            ]}
                        />
                        
                        <div className={styles.adminCard}>
                            <h3 className={styles.adminSectionTitle}>Pending Orders / Actions</h3>
                            <div className={styles.tableResponsive}>
                                <table className={styles.analyticsTable}>
                                    <thead><tr><th>Order ID</th><th>Store</th><th>Total</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {analytics.pending_orders.length === 0 && <tr><td colSpan="4">No pending orders.</td></tr>}
                                        {analytics.pending_orders.map(order => (
                                            <tr key={order.order_id}>
                                                <td data-label="Order ID">{order.order_id}</td>
                                                <td data-label="Store">{order.store_name}</td>
                                                <td data-label="Total" style={{textAlign: 'right'}}>${order.total.toFixed(2)}</td>
                                                <td data-label="Status">{order.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className={styles.adminCard}>
                            <h3 className={styles.adminSectionTitle}>Order Timeline (Recent 10)</h3>
                             <div className={styles.tableResponsive}>
                                <table className={styles.analyticsTable}>
                                    <thead><tr><th>Date</th><th>Store</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {analytics.order_timeline.length === 0 && <tr><td colSpan="3">No order history.</td></tr>}
                                        {analytics.order_timeline.map(item => (
                                            <tr key={item.id}>
                                                <td data-label="Date">{new Date(item.date).toLocaleString()}</td>
                                                <td data-label="Store">{item.store}</td>
                                                <td data-label="Status">{item.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        <AnalyticsTable
                            title="Territory Map (Orders by Area)"
                            data={analytics.territory_map_data}
                            columns={[
                                { key: 'city', label: 'City' },
                                { key: 'orders', label: 'Total Orders' },
                            ]}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default SalesrepDashboard;